import { Fragment, useEffect, useRef } from 'react';

import { ExtendStep, ReviewStep, SelectStep } from 'pages/orders/extend_order/steps';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { ExtendOrderContext } from 'pages/orders/extend_order/context';
import { diffDays, formatDay, MS_PER_DAY, parseDateOnly } from 'utils';
import { PartnerApi, SelectedPartner } from 'apis/PartnerApi';
import { HelpSidebarTrigger } from 'components/help_sidebar';
import { Order, OrderApi, OrderStatus } from 'apis/OrderApi';
import { helpContent } from 'pages/orders/extend_order/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { DefaultLayout } from 'components/layout';

import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

import { useSuperState } from 'hooks';

enum EStep {
    SELECT,
    EXTEND,
    CONFIRM
}

export type ExtendOrderContextValue = {
    confirmed: boolean;
    error: string;
    expireDays: number;
    extendedBy: string;
    handleBack: () => void;
    handleChangeConfirmed: (confirmed: boolean) => void;
    handleChangeExpire: (newExpireDate: Date | null) => void;
    handleChangeExtendedBy: (extendedBy: string) => void;
    handleChangeRequestedBy: (requestedBy: string) => void;
    handleChangeStart: (newStartDate: Date | null) => void;
    handleConfirm: () => Promise<void>;
    handleNext: () => void;
    handleSelectOrder: (order: Order) => Promise<void>;
    loading: boolean;
    newExpireDate: Date;
    newStartDate: Date;
    orders: Order[];
    partnerName: string;
    requestedBy: string;
    requiresConfirmation: boolean;
    selectedOrder: Order;
    startDays: number;
    step: EStep;
}

const WARN_THRESHOLD = (startDays: number, expireDays: number): boolean => expireDays > 60 ||
    expireDays < 0 ||
    startDays > 0 ||
    startDays < -60 ||
    Math.abs(startDays) + expireDays > 60;

const DEFAULT_STATE = {
    confirmed: false,
    error: '',
    expireDays: 0,
    extendedBy: '',
    newExpireDate: null as Date | null,
    newStartDate: null as Date | null,
    requestedBy: '',
    selectedOrder: null as Order | null,
    startDays: 0,
    step: EStep.SELECT as EStep
};

export const ExtendOrder = props => {

    const [state, setState] = useSuperState({
        ...DEFAULT_STATE,
        loading: true,
        orders: [] as Order[],
        partner: null as SelectedPartner | null
    });
    const { confirmed, error, expireDays, extendedBy, loading, newExpireDate, newStartDate, orders, partner, requestedBy,
        selectedOrder, startDays, step } = state;

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const intl = useIntl();

    const handleChangeExtendedBy = (extendedBy: string) => setState({ extendedBy });

    const handleChangeRequestedBy = (requestedBy: string) => setState({ requestedBy });

    const handleChangeConfirmed = (confirmed: boolean) => setState({ confirmed });

    const handleBack = () => {

        if (step === EStep.SELECT) return;

        setState({
            error: '',
            step: step - 1
        });

    };

    const handleStepperChange = (step: EStep) => {

        if (step === state.step) return;

        if (step > state.step) {

            handleNext();

        } else {

            setState({
                confirmed: false,
                error: '',
                step
            });

        }

    };

    const fetchPartnerAndOrders = async () => {

        try {

            setState({ loading: true });
            const selectedPartner = await PartnerApi.GetSelectedPartner();

            if (!selectedPartner) {

                window.location.href = `/`;
                return;

            }

            const data = await OrderApi.GetAll(selectedPartner.ID, OrderStatus.Processed, false);
            const sorted = [...data].sort((a, b) => {

                const da = a.DateExpire ? new Date(a.DateExpire).getTime() : 0;
                const db = b.DateExpire ? new Date(b.DateExpire).getTime() : 0;
                return db - da;

            });

            setState({
                loading: false,
                orders: sorted,
                partner: selectedPartner
            });

        } catch (e) {

            setState({
                error: e?.message ?? 'Failed to load orders.',
                loading: false
            });

        }

    };

    const handleSelectOrder = async (order: Order) => {

        try {

            setState({
                error: '',
                loading: true
            });

            const details = await OrderApi.GetOrderDetails(order.ID);

            // parseDateOnly reads the literal calendar day from the server string,
            // immune to timezone shifts and time-of-day components (e.g. 23:59:59).
            const startDate = parseDateOnly(details.DateStart);
            const expireDate = parseDateOnly(details.DateExpire);

            setState({
                confirmed: false,
                loading: false,
                newExpireDate: expireDate,
                newStartDate: startDate,
                selectedOrder: details,
                step: EStep.EXTEND
            });

        } catch (e) {

            setState({ error: e?.message ?? 'Failed to load order details.', loading: false });

        }

    };

    const handleChangeStart = (date: Date | null) => {

        if (!date || !selectedOrder) return;

        const deltaDays = diffDays(selectedOrder.DateStart, date);

        if (selectedOrder.CodeCount > 0 && deltaDays !== 0) {

            setState({ error: '**Cannot set to a different start date — access codes have been created.' });
            return;

        }

        const currentExpire = newExpireDate ?? parseDateOnly(selectedOrder.DateExpire) ?? parseDateOnly(new Date());
        if (!currentExpire) return;

        const newExpire = new Date(currentExpire.getTime() + deltaDays * MS_PER_DAY);

        setState({ error: '', newExpireDate: newExpire, newStartDate: date });

    };

    const handleChangeExpire = (newExpireDate: Date | null) => {

        setState({
            error: '',
            newExpireDate
        });

    };

    const handleNext = () => {

        if (!selectedOrder || !newStartDate || !newExpireDate || step === EStep.CONFIRM) return;

        // diffDays compares literal calendar days, so time-of-day components on the
        // stored dates (e.g. 23:59:59) can never skew the extension deltas.
        const sdays = diffDays(selectedOrder.DateStart, newStartDate);
        const edays = diffDays(selectedOrder.DateExpire, newExpireDate);

        setState({
            confirmed: false,
            expireDays: edays,
            startDays: sdays,
            step: step + 1
        });

    };

    const handleConfirm = async () => {

        if (!selectedOrder) return;

        try {

            setState({ loading: true });
            const today = formatDay(new Date(), intl);
            const comment = `Extended by ${extendedBy} per ${requestedBy} ${startDays} Days on Start and ${expireDays} Days on End on the Date ${today}`;
            const result = await OrderApi.Update(selectedOrder.ID, comment, startDays, expireDays);

            if (result.Done) {

                setState({ ...DEFAULT_STATE });
                createToast(formatMessage({ id: 'order_extended_successfully.f72e10f6', defaultMessage: 'Order extended successfully.' }), 'success');
                await fetchPartnerAndOrders();

            } else {

                setState({ loading: false });
                createToast(result.Message || formatMessage({ id: 'failed_to_extend_order.da35ce21', defaultMessage: 'Failed to extend order.' }), 'danger');

            }

        } catch (e) {

            setState({ loading: false });
            createToast(e?.message ?? 'An unexpected error occurred.', 'danger');

        }

    };

    useEffect(() => {

        fetchPartnerAndOrders();

    }, []);

    const context: ExtendOrderContextValue = {
        confirmed,
        error,
        expireDays,
        extendedBy,
        handleBack,
        handleChangeConfirmed,
        handleChangeExpire,
        handleChangeExtendedBy,
        handleChangeRequestedBy,
        handleChangeStart,
        handleConfirm,
        handleNext,
        handleSelectOrder,
        loading,
        newExpireDate,
        newStartDate,
        orders,
        partnerName: partner?.Name ?? '',
        requestedBy,
        requiresConfirmation: WARN_THRESHOLD(startDays, expireDays),
        selectedOrder,
        startDays,
        step
    };

    const Steps = [SelectStep, ExtendStep, ReviewStep];
    const StepComponent = Steps[step] || Fragment;

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'extend_order.007fe725', defaultMessage: 'Extend Order' })}>
            <ExtendOrderContext.Provider value={context}>
                <div className={'extend-order'}>

                    <div className={'d-flex flex-column gap-5'}>

                        <div>
                            <div className="d-flex align-items-center justify-content-between">
                                <h2>
                                    <FormattedMessage defaultMessage="Extend Order" id="extend_order.007fe725" />
                                </h2>
                                <HelpSidebarTrigger content={helpContent} />
                            </div>

                            <hr />

                            <GMetrixStepper
                                linear
                                stepItems={[
                                    { label: formatMessage({ id: 'choose_order.51197e04', defaultMessage: 'Choose Order' }) },
                                    { label: formatMessage({ id: 'select_dates.7057afee', defaultMessage: 'Select Dates' }) },
                                    { label: formatMessage({ id: 'review_and_submit.8df4b3e8', defaultMessage: 'Review & Submit' }) }
                                ]}
                                handleChange={e => handleStepperChange(e.value)}
                                setStep={s => setState({ step: typeof s === 'function' ? s(step) : s })}
                                step={step}
                            />
                        </div>

                        <StepComponent/>

                    </div>

                </div>
            </ExtendOrderContext.Provider>
        </DefaultLayout>
    );

};
